// Phone number detection and formatting utilities
import React from 'react';
import { homePhonePrefixes, mobilePhonePrefixes, mobilePhonePrefixesAbroad, countryPhonePrefixes } from '../models/enums';
import { createPhoneNumberRevealButton } from '../components/PhoneNumberRevealButton';

// Build phone number patterns
const homePhonePrefixPattern = homePhonePrefixes.join('|');
const allMobilePhonePrefixPattern = [...mobilePhonePrefixes, ...mobilePhonePrefixesAbroad].join('|');

// Build international prefix pattern from countryPhonePrefixes
// Escape + signs and allow optional spaces within the prefix (e.g., "00 385" or "00385")
const countryPrefixPattern = countryPhonePrefixes
  .map(prefix => {
    const escaped = prefix.replace('+', '\\+');
    // Allow optional space after + or 00
    if (escaped.startsWith('\\+')) {
      return escaped.replace('\\+', '\\+\\s*');
    } else if (escaped.startsWith('00')) {
      return escaped.replace('00', '00\\s*');
    }
    return escaped;
  })
  .join('|');

// Build pattern for country codes without prefix (e.g., "385", "386", "49")
const countryCodeOnlyPattern = countryPhonePrefixes
  .map(prefix => {
    // Extract just the numeric code: "+385" -> "385", "00385" -> "385"
    return prefix.replace(/^\+|^00/, '');
  })
  .join('|');

// Phone number regex patterns:
// Comprehensive support for various phone formats including:
//
// International formats (supports: +385/00385, +386/00386, +49/0049):
//   - +385/91/123/4567, +385/1/234/5678 (with slashes)
//   - +385 91 123 45 67, +385-91-123-45-67 (spaces/dashes)
//   - +38591123456, +3851234567 (no separators)
//   - +385 91 12 34 567 (variable groupings)
//   - +385 (0)91 123 4567 (with (0) notation)
//   - (+385) 91 123 4567, (+385 91) 123 4567 (parentheses around country code)
//   - +385 91 / 123 4567 (mixed separators)
//   - 00 385 95 289 4518 (space in country prefix)
//   - 385996585188 (no + or 00 prefix)
//   - +49 1733295536 (without leading 0 in area code)
//   - +385 (1)4920800 (area code in parentheses without leading 0)
//
// National formats (Croatian):
//   - 0911234567, 012345678 (continuous, no separators)
//   - 091 123 45 67, 01 234 5678 (with spaces)
//   - 091-123-45-67, 01-234-5678 (with dashes)
//   - 091 12 34 567 (variable groupings)
//   - (091) 123 4567, (091) 123-4567 (with parentheses)
//
// Excluded patterns:
//   - Date formats like 01/2026, 12/24, etc. (using negative lookahead)
const PHONE_REGEX = new RegExp(
  `(?:^|\\s|[^\\d])` + // Word boundary or non-digit before
  `(?!\\d{2}[-/]\\d{4}(?:\\s|$|[^\\d]))` + // Negative lookahead: exclude date formats (MM/YYYY only - 2 digits + separator + 4 digits)
  `(` + // Capture group for the actual phone number
    // Pattern 1: International with parentheses around country code
    // Examples: (+385) 91 123 4567, (+385 91) 123 4567, (+385)/91/123/4567
    `\\((?:${countryPrefixPattern})(?:\\s*(?:${allMobilePhonePrefixPattern.replace(/0/g, '')}|${homePhonePrefixPattern.replace(/0/g, '')}))?\\)[-\\s/]*(?:${allMobilePhonePrefixPattern.replace(/0/g, '')}|${homePhonePrefixPattern.replace(/0/g, '')})?[-\\s/]*\\d{1,4}(?:[-\\s/]*\\d{1,4}){1,3}` +
    `|` +
    // Pattern 2: International with (0) notation
    // Examples: +385 (0)91 123 4567, 00 385 (0)91 555 3533
    `(?:${countryPrefixPattern})[-\\s/]*\\(\\s*0\\s*\\)[-\\s/]*(?:${allMobilePhonePrefixPattern.replace(/0/g, '')}|${homePhonePrefixPattern.replace(/0/g, '')})[-\\s/]*\\d{1,4}(?:[-\\s/]*\\d{1,4}){1,3}` +
    `|` +
    // Pattern 3: International with area code in parentheses (without leading 0)
    // Examples: +385 (1)4920800, +49 (173)3295536
    `(?:${countryPrefixPattern})[-\\s/]*\\(\\s*(?:\\d{1,4})\\s*\\)[-\\s/]*\\d{1,4}(?:[-\\s/]*\\d{1,4}){0,3}` +
    `|` +
    // Pattern 4: International standard formats with leading 0 in area code
    // Examples: +385 91 123 4567, +385/91/123/4567, 00 385 95 289 4518, +381 60 123 4567
    `(?:${countryPrefixPattern})[-\\s/]*(?:${allMobilePhonePrefixPattern.replace(/0/g, '')}|${homePhonePrefixPattern.replace(/0/g, '')})[-\\s/]*\\d{1,4}(?:[-\\s/]*\\d{1,4}){1,3}` +
    `|` +
    // Pattern 5: International without leading 0 in area code
    // Examples: +49 1733295536
    `(?:${countryPrefixPattern})[-\\s/]*\\d{2,4}[-\\s/]*\\d{1,4}(?:[-\\s/]*\\d{1,4}){0,3}` +
    `|` +
    // Pattern 5b: International with country code but no prefix (+ or 00)
    // Examples: 385996585188, 38591234567, 381604864882
    `(?:${countryCodeOnlyPattern})(?:${allMobilePhonePrefixPattern.replace(/0/g, '')}|${homePhonePrefixPattern.replace(/0/g, '')}|\\d{1,3})\\d{6,8}` +
    `|` +
    // Pattern 6: National with parentheses
    // Examples: (091) 123 4567, (091) 123-4567, (01) 234 5678
    `\\((?:${allMobilePhonePrefixPattern}|${homePhonePrefixPattern})\\)[-\\s/]*\\d{1,4}(?:[-\\s/]*\\d{1,4}){1,3}` +
    `|` +
    // Pattern 7: National with separators (at least one separator required)
    // Examples: 091 123 45 67, 091-123-45-67, 091 12 34 567, 01 234 5678
    `(?:${allMobilePhonePrefixPattern}|${homePhonePrefixPattern})[-\\s/]+\\d{1,4}(?:[-\\s/]*\\d{1,4}){1,3}` +
    `|` +
    // Pattern 8: National continuous (no separators)
    // Examples: 0911234567, 012345678
    `(?:${allMobilePhonePrefixPattern}|${homePhonePrefixPattern})\\d{6,7}` +
  `)` +
  `(?=\\s|$|[^\\d])`, // Word boundary or non-digit after
  'g'
);

interface GAEvent {
  category: string;
  action: string;
  label: string;
}

// Matches Latin letters including Croatian diacritics. Used to detect
// alphanumeric identifiers (part numbers, model codes) so we don't mask
// them as phone numbers — real phone numbers never contain letters.
const LETTER_REGEX = /[a-zA-ZćčšžđĆČŠŽĐ]/;

/**
 * Replace phone numbers in text with clickable links
 * Returns React elements for rendering
 */
const linkifyPhoneNumbers = (text: string, gaEvent: GAEvent, adId?: string): (string | JSX.Element)[] => {
  if (!text) return [text];

  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;

  // Create a new regex instance for each search to avoid state issues
  const matches = [...text.matchAll(new RegExp(PHONE_REGEX.source, 'g'))];

  matches.forEach((match, idx) => {
    const fullMatch = match[0];
    const phoneNumber = match[1]; // Use captured group which is the actual phone number
    const matchStartIndex = match.index !== undefined ? match.index : 0;

    // Calculate where the phone number actually starts (after any leading boundary chars)
    const leadingBoundaryLength = fullMatch.indexOf(phoneNumber);
    const phoneStartIndex = matchStartIndex + leadingBoundaryLength;
    const phoneEndIndex = phoneStartIndex + phoneNumber.length;

    // Skip when the digit run is glued to letters on either side — e.g.
    // "HX145LCR-21K4-32181" part numbers should not get masked. The regex's
    // non-digit boundary is too permissive since letters count as non-digits.
    const charBefore = phoneStartIndex > 0 ? text.charAt(phoneStartIndex - 1) : '';
    const charAfter = text.charAt(phoneEndIndex);
    if (LETTER_REGEX.test(charBefore) || LETTER_REGEX.test(charAfter)) {
      return;
    }

    // Add text before phone number
    if (phoneStartIndex > lastIndex) {
      parts.push(text.substring(lastIndex, phoneStartIndex));
    }

    // Add clickable phone number button
    parts.push(
      createPhoneNumberRevealButton(phoneNumber, idx, gaEvent, adId, `phone-${idx}`)
    );

    lastIndex = phoneEndIndex;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

/**
 * Process text with line breaks and phone numbers as React elements
 * Handles both line breaks (as <br> tags) and phone number buttons
 * Should be used with text that has been processed by replaceLineBreaksByTag
 */
export const processTextWithPhoneNumbers = (textWithBrTags: string, gaEvent: GAEvent, adId?: string) => {
  if (!textWithBrTags) return textWithBrTags;

  // Split by <br> tags that were created by replaceLineBreaksByTag
  const lines = textWithBrTags.split('<br>');

  // Process each line for phone numbers
  const elements = [];
  lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) {
      elements.push(React.createElement('br', { key: `br-${lineIndex}` }));
    }

    // Apply phone number linkification to each line
    const lineElements = linkifyPhoneNumbers(line, gaEvent, adId);
    lineElements.forEach((element, idx) => {
      if (typeof element === 'string') {
        elements.push(element);
      } else {
        // Clone element with unique key
        const elementWithKey = React.cloneElement(element, {
          key: `phone-${lineIndex}-${idx}`
        });
        elements.push(elementWithKey);
      }
    });
  });

  return React.createElement(React.Fragment, null, elements);
};



// WEBPACK FOOTER //
// ./src/utils/phoneUtils.ts